import { Injectable } from '@angular/core';
import { DateTime } from 'luxon';
import posthog from 'posthog-js';
import { BehaviorSubject, map, mergeMap, tap } from 'rxjs';
import { ResourceName } from '../../../../../backend/src/core/resource-name';
import { LambdaService } from '../../shared/core/lambda.service';
import { FileUtils } from '../../shared/utils/file-utils';
import { CoreItemType } from '../core-item/core-item';
import { CoreItemService } from '../core-item/core-item.service';
import { PostHogEvent } from '../posthog/posthog.event';
import { ResumeGate, SaveResumeRequest } from './../../../../../backend/src/core/resume/resume.gate';
import { WorkspaceHolderService } from './../workspace/workspace.holder.service';
import { Resume, ResumeSubData } from './resume';

@Injectable({
  providedIn: 'root',
})
export class ResumeService {
  private _changeSubject = new BehaviorSubject<Resume | undefined>(undefined);

  constructor(
    private coreItemService: CoreItemService,
    private workspaceHolderService: WorkspaceHolderService,
    private lambdaService: LambdaService
  ) {}

  getResumeById$(id: string) {
    return this.coreItemService.getItemByAction$({ id }).pipe(map((c) => c as Resume));
  }

  resumeList$() {
    return this._changeSubject.asObservable().pipe(
      mergeMap((_) => {
        return this.coreItemService.getCoreItemList$([CoreItemType.resume]).pipe(
          map((list) => (list ?? []).filter((item) => item.coreType === CoreItemType.resume) as Resume[]),
          map((list) =>
            list.sort((a, b) => ((a.createdTime ?? DateTime.local()) < (b.createdTime ?? DateTime.local()) ? 1 : -1))
          )
        );
      })
    );
  }

  uploadResume$(resumeId: string, fileName: string, fileData: File) {
    return FileUtils.readAsString$(fileData).pipe(
      mergeMap((base64Content) => {
        const parts = base64Content.split(',');
        const noHeadContent = parts[1];
        const contentType = parts[0].split(';')[0].split(':')[1];

        posthog.capture(PostHogEvent.resumeAdd, { resumeId, fileSize: fileData.size });

        const request: SaveResumeRequest = {
          resumeId,
          workspaceId: this.workspaceHolderService.currentWorkspace().id,
          fileName,
          base64Content: noHeadContent,
          contentType,
        };
        return this.lambdaService.run$(ResourceName.resume, ResumeGate.actions.saveResume, request);
      })
    );
  }

  addResume$(title: string, subData: ResumeSubData) {
    return this.coreItemService
      .batchWriteMetadataAndRelationList$({
        addMetadataList: [
          {
            title,
            coreType: CoreItemType.resume,
            subData,
          },
        ],
      })
      .pipe(
        map((arr) => {
          this._changeSubject.next(arr[0] as Resume);
          return arr[0] as Resume;
        })
      );
  }

  deleteResumeList$(resumeList: Resume[]) {
    const deleteMetadataList = resumeList.map((c) => ({
      id: c.id,
      sk: c.sk,
    }));

    posthog.capture(PostHogEvent.resumeDelete, {
      resumeList: deleteMetadataList.map((c) => c.id).join(','),
      deleteCount: deleteMetadataList.length,
    });

    return this.coreItemService
      .batchWriteMetadataAndRelationList$({
        deleteMetadataList,
      })
      .pipe(
        tap((_) => {
          this._changeSubject.next(undefined);
        })
      );
  }
}
